import type { WindowId } from '../framework/types';

export const DEBUG_SUITE_VIEW_STORAGE_KEY = 'debug_suite_view_v1';
export const DEBUG_SUITE_SCALE_MIN = 0.75;
export const DEBUG_SUITE_SCALE_MAX = 1.25;
export const DEBUG_SUITE_SCALE_STEP = 0.1;

export type DebugSuiteLayoutId = 'default' | 'compact-left' | 'wide-center' | 'right-inspector';

export interface DebugSuiteGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DebugSuiteViewState {
  scale: number;
  layout: DebugSuiteLayoutId;
}

export const DEBUG_SUITE_LAYOUT_OPTIONS: ReadonlyArray<{
  id: DebugSuiteLayoutId;
  label: string;
}> = [
  { id: 'default', label: 'Default' },
  { id: 'compact-left', label: 'Compact left' },
  { id: 'wide-center', label: 'Wide center' },
  { id: 'right-inspector', label: 'Right inspector' },
];

export function clampDebugSuiteScale(value: number): number {
  if (!Number.isFinite(value)) return 1;
  const clamped = Math.max(DEBUG_SUITE_SCALE_MIN, Math.min(DEBUG_SUITE_SCALE_MAX, value));
  return Math.round(clamped * 100) / 100;
}

export function isDebugSuiteLayoutId(value: string | null): value is DebugSuiteLayoutId {
  return DEBUG_SUITE_LAYOUT_OPTIONS.some((option) => option.id === value);
}

export function resolveDebugSuiteLayout(
  layout: DebugSuiteLayoutId,
  viewport: { width: number; height: number },
): DebugSuiteGeometry {
  const availableWidth = Math.max(480, viewport.width - 48);
  const availableHeight = Math.max(320, viewport.height - 140);
  switch (layout) {
  case 'compact-left':
    return { x: 24, y: 96, width: Math.min(560, availableWidth), height: Math.min(580, availableHeight) };
  case 'wide-center': {
    const width = Math.min(1040, availableWidth);
    return {
      x: Math.max(24, Math.round((viewport.width - width) / 2)),
      y: 110,
      width,
      height: Math.min(780, availableHeight),
    };
  }
  case 'right-inspector': {
    const width = Math.min(640, availableWidth);
    return {
      x: Math.max(24, viewport.width - width - 24),
      y: 96,
      width,
      height: Math.min(760, availableHeight),
    };
  }
  case 'default':
    return {
      x: Math.min(280, Math.max(24, viewport.width - 724)),
      y: 110,
      width: Math.min(700, availableWidth),
      height: Math.min(720, availableHeight),
    };
  }
}

function finiteParam(params: URLSearchParams, name: string): number | undefined {
  const raw = params.get(name);
  if (raw == null || raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function readDebugSuiteViewFromUrl(url: URL): {
  scale?: number;
  layout?: DebugSuiteLayoutId;
  geometry?: Partial<DebugSuiteGeometry>;
} {
  const layoutRaw = url.searchParams.get('dbgHubLayout');
  const layout = isDebugSuiteLayoutId(layoutRaw) ? layoutRaw : undefined;
  const scaleRaw = finiteParam(url.searchParams, 'dbgHubScale');
  const geometry = {
    x: finiteParam(url.searchParams, 'dbgHubX'),
    y: finiteParam(url.searchParams, 'dbgHubY'),
    width: finiteParam(url.searchParams, 'dbgHubW'),
    height: finiteParam(url.searchParams, 'dbgHubH'),
  };
  const hasGeometry = Object.values(geometry).some((value) => value != null);
  return {
    scale: scaleRaw == null ? undefined : clampDebugSuiteScale(scaleRaw),
    layout,
    geometry: hasGeometry ? geometry : undefined,
  };
}

export function buildDebugSuiteShareUrl(
  currentUrl: URL,
  selectedTools: readonly WindowId[],
  view: DebugSuiteViewState,
  geometry: DebugSuiteGeometry,
): string {
  const url = new URL(currentUrl.toString());
  url.searchParams.delete('dbgPreset');
  url.searchParams.set('dbgTools', selectedTools.join(','));
  url.searchParams.set('dbgHubScale', String(clampDebugSuiteScale(view.scale)));
  url.searchParams.set('dbgHubLayout', view.layout);
  url.searchParams.set('dbgHubX', String(Math.round(geometry.x)));
  url.searchParams.set('dbgHubY', String(Math.round(geometry.y)));
  url.searchParams.set('dbgHubW', String(Math.round(geometry.width)));
  url.searchParams.set('dbgHubH', String(Math.round(geometry.height)));
  return url.toString();
}
